
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Download, FileText, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProductForm } from "../../../inventory/ProductForm";
import { MovementForm } from "../../../inventory/MovementForm";
import { InventoryStats } from "../../../inventory/InventoryStats";
import { PeriodForm } from "../../../inventory/PeriodForm";
import { PeriodList } from "../../../inventory/PeriodList";
import { InventoryReport } from "../../../inventory/InventoryReport";
import { useToast } from "@/hooks/use-toast";
import { DeleteInventoryProductDialog } from "./DeleteInventoryProductDialog";

export function InventoryManagement() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("periods");
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [isMovementFormOpen, setIsMovementFormOpen] = useState(false);
  const [isPeriodFormOpen, setIsPeriodFormOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [productToDelete, setProductToDelete] = useState<{id: string, name: string} | null>(null);

  const { data: periods, isLoading: isLoadingPeriods } = useQuery({
    queryKey: ["inventory-periods"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("inventory_periods")
        .select("*")
        .eq("professional_id", user.id)
        .order("start_date", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  const { data: products, isLoading: isLoadingProducts } = useQuery({
    queryKey: ["inventory-products", selectedPeriodId],
    queryFn: async () => {
      if (!selectedPeriodId) return [];

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("inventory_products")
        .select("*")
        .eq("professional_id", user.id)
        .eq("period_id", selectedPeriodId)
        .order("name");

      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedPeriodId,
  });

  const handleExportInventory = async () => {
    if (!selectedPeriodId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une période d'inventaire",
        variant: "destructive"
      });
      return;
    }

    // L'exportation sera gérée par le composant InventoryReport
    setActiveTab("report");
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Gestion d'inventaire</h2>
        <Button onClick={handleExportInventory} className="gap-2">
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Exporter PDF</span>
        </Button>
      </div>

      <Tabs defaultValue="periods" className="w-full" onValueChange={setActiveTab} value={activeTab}>
        <TabsList className="mb-4 flex flex-wrap">
          <TabsTrigger value="periods">Périodes</TabsTrigger>
          <TabsTrigger value="products" disabled={!selectedPeriodId}>Produits</TabsTrigger>
          <TabsTrigger value="stats" disabled={!selectedPeriodId}>Statistiques</TabsTrigger>
          <TabsTrigger value="report" disabled={!selectedPeriodId}>Rapport</TabsTrigger>
        </TabsList>
        
        <TabsContent value="periods" className="space-y-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setIsPeriodFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle période
            </Button>
          </div>
          
          <PeriodList 
            periods={periods || []} 
            isLoading={isLoadingPeriods}
            onPeriodSelect={(periodId) => {
              setSelectedPeriodId(periodId);
              setActiveTab("products");
            }} 
          />
        </TabsContent>
        
        <TabsContent value="products" className="space-y-4">
          {selectedPeriodId && (
            <>
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  Produits pour la période: {
                    periods?.find(p => p.id === selectedPeriodId)?.period_name
                  }
                </h3>
                <div className="flex gap-2">
                  <Button onClick={() => setIsProductFormOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau produit
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsMovementFormOpen(true);
                      setSelectedProductId(null);
                    }}
                    disabled={!products?.length}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Mouvement
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {isLoadingProducts ? (
                  <p>Chargement des produits...</p>
                ) : products?.length ? (
                  products.map((product) => (
                    <Card key={product.id} className="overflow-hidden">
                      <CardHeader className="p-4">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg">{product.name}</CardTitle>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setProductToDelete({
                              id: product.id,
                              name: product.name
                            })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <CardDescription>{product.dosage}</CardDescription>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="font-medium">Fabricant:</p>
                            <p className="text-muted-foreground">{product.manufacturer || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="font-medium">Emballage:</p>
                            <p className="text-muted-foreground">{product.packaging}</p>
                          </div>
                          <div>
                            <p className="font-medium">Stock minimum:</p>
                            <p className="text-muted-foreground">{product.minimum_threshold || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="font-medium">Prix unitaire:</p>
                            <p className="text-muted-foreground">{product.unit_price ? `${product.unit_price} €` : 'N/A'}</p>
                          </div>
                        </div>
                        <div className="mt-4">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full"
                            onClick={() => {
                              setSelectedProductId(product.id);
                              setIsMovementFormOpen(true);
                            }}
                          >
                            Ajouter mouvement
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card className="col-span-full">
                    <CardContent className="p-6 text-center">
                      <p className="mb-4">Aucun produit trouvé pour cette période.</p>
                      <Button onClick={() => setIsProductFormOpen(true)}>
                        Ajouter un produit
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </TabsContent>
        
        <TabsContent value="stats">
          {selectedPeriodId && (
            <InventoryStats periodId={selectedPeriodId} />
          )}
        </TabsContent>
        
        <TabsContent value="report">
          {selectedPeriodId && (
            <InventoryReport periodId={selectedPeriodId} />
          )}
        </TabsContent>
      </Tabs>

      {selectedPeriodId && (
        <>
          <ProductForm
            open={isProductFormOpen}
            onOpenChange={setIsProductFormOpen}
            periodId={selectedPeriodId}
          />
          
          <MovementForm
            open={isMovementFormOpen}
            onOpenChange={setIsMovementFormOpen}
            productId={selectedProductId || ''}
            periodId={selectedPeriodId}
          />
        </>
      )}
      
      <PeriodForm 
        open={isPeriodFormOpen} 
        onOpenChange={setIsPeriodFormOpen} 
      />

      {productToDelete && (
        <DeleteInventoryProductDialog
          open={!!productToDelete}
          onOpenChange={() => setProductToDelete(null)}
          productId={productToDelete.id}
          productName={productToDelete.name}
        />
      )}
    </div>
  );
}
